import { Fragment, useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  motion, MotionConfig, useScroll, useTransform, type Variants,
} from 'framer-motion';
import { Check, ArrowRight, Plug, Moon, Sun, LineChart } from 'lucide-react';
import ClearPathLogo from '../components/ClearPathLogo';
import { DETECTOR_LABELS } from '../components/BriefCard';
import UsualRangeStrip from '../components/UsualRangeStrip';
import { PRO_PRICE_LABEL, FOUNDING_PRICE_LABEL } from '../pricing';
import { fmt, fmtCount, fmtSigned } from '../utils/formatters';
import { SAMPLE_MOVES, SAMPLE_OUTCOME, SAMPLE_DAY, sampleSubject, dailyScore, scoreHeadline } from './landingSample';

// ---------------------------------------------------------------------------
// Motion helpers
// ---------------------------------------------------------------------------

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: EASE, delay: i * 0.07 },
  }),
};

/** Keyboard focus: the plum ring DESIGN.md asks for, shown only when focus came from the keyboard. */
const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]';
const FOCUS_ON_DARK = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#2D2A2E]';

// ---------------------------------------------------------------------------
// The signature moment — a Daily Brief that arrives (it comes to you)
// ---------------------------------------------------------------------------

const briefList: Variants = {
  hidden: {},
  show: { transition: { delayChildren: 1.3, staggerChildren: 0.22 } },
};
const briefItem: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
};

function AnimatedBrief() {
  const score = dailyScore(SAMPLE_DAY.yesterday, SAMPLE_DAY.baseline);

  return (
    <figure className="relative">
      <div className="absolute -inset-6 bg-[radial-gradient(ellipse_at_top,_rgba(74,45,94,0.18),_transparent_70%)] blur-2xl pointer-events-none" aria-hidden="true" />

      {/* the card glides up and settles — an email arriving, not one being written */}
      <motion.div
        initial={{ opacity: 0, y: 44 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.05, ease: EASE }}
        className="relative bg-white border border-[#E6E2DE] rounded-[18px] shadow-[0_24px_70px_-24px_rgba(74,45,94,0.45)] overflow-hidden"
      >
        {/* masthead — the mark, as on the email itself */}
        <div className="bg-[#4A2D5E] px-5 py-3 flex items-center gap-3">
          <ClearPathLogo variant="white" size="sm" />
          <div className="min-w-0">
            <p className="font-sans text-[13px] font-medium text-white leading-tight">ClearPath · Daily Brief</p>
            <p className="font-sans text-[11px] text-white/65 leading-tight">Tuesday · 7:00 AM</p>
          </div>
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.5, ease: EASE }}
            className="ml-auto flex items-center gap-1.5 text-[10px] font-sans text-white/80 bg-white/10 px-2 py-1 rounded-full whitespace-nowrap"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#7BA68E]" aria-hidden="true" /> Just arrived
          </motion.span>
        </div>

        {/* subject — summed from the moves below, so it can never disagree with them */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.65, ease: EASE }}
          className="px-5 pt-3.5 pb-3 border-b border-[#F0EDE9]"
        >
          <p className="font-sans text-[10px] font-medium uppercase tracking-[0.14em] text-[#6B6670] mb-1">Subject</p>
          <p className="font-serif text-[17px] text-[#2D2A2E] leading-snug">{sampleSubject(SAMPLE_MOVES)}</p>
        </motion.div>

        {/* yesterday against the shop's own weekday — the score and the usual range, as the email draws them */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.95, duration: 0.6 }}
          className="px-5 py-3 border-b border-[#F0EDE9] bg-[#FAF9F7]/70"
        >
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-full bg-[#4A2D5E] text-white font-sans text-[13px] font-bold tabular-nums flex items-center justify-center flex-shrink-0">
              <span className="sr-only">Score </span>{fmtCount(score)}<span className="sr-only"> of 100</span>
            </span>
            <p className="font-serif text-[13.5px] text-[#2D2A2E] leading-snug">{scoreHeadline(score, SAMPLE_DAY.weekday)}</p>
          </div>
          <UsualRangeStrip
            className="mt-2.5"
            yesterday={SAMPLE_DAY.yesterday}
            usual={SAMPLE_DAY.usual}
            weekday={SAMPLE_DAY.weekday}
          />
        </motion.div>

        {/* moves — one gentle, orchestrated cascade (static numbers, no ticking) */}
        <motion.div variants={briefList} initial="hidden" animate="show" className="p-3.5 space-y-2.5">
          {SAMPLE_MOVES.map((m, i) => (
            <motion.div
              key={m.detector}
              variants={briefItem}
              className="rounded-[12px] border border-[#E6E2DE] bg-[#FAF9F7] p-3"
            >
              <div className="flex items-start gap-3">
                {/* numbered plum chip — the move card's mark in the app and the email */}
                <span className="w-6 h-6 rounded-full bg-[#4A2D5E] text-white flex items-center justify-center font-sans text-xs font-medium flex-shrink-0 mt-0.5" aria-hidden="true">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-[#4A2D5E]">{DETECTOR_LABELS[m.detector]}</p>
                      <p className="font-sans text-[13px] font-medium text-[#2D2A2E] leading-snug mt-0.5">{m.title}</p>
                      <p className="font-sans text-[11.5px] text-[#6B6670] mt-0.5 leading-snug">{m.why}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-sans text-[13px] font-semibold text-[#2E6B4F] tabular-nums whitespace-nowrap">
                        {fmt(m.low)}–{fmt(m.high)}
                      </p>
                      <p className="font-sans text-[10px] text-[#6B6670]">/mo, estimated</p>
                    </div>
                  </div>
                  {/* the two answers, as drawn — a picture of the email's buttons, not controls */}
                  <div className="flex items-center gap-2 mt-2.5" aria-hidden="true">
                    <span className="flex-1 text-center text-[11px] font-sans font-medium text-white bg-[#2E6B4F] rounded-[7px] py-1.5">
                      I&rsquo;ll do this
                    </span>
                    <span className="flex-1 text-center text-[11px] font-sans text-[#6B6670] border border-[#E6E2DE] bg-white rounded-[7px] py-1.5">
                      Not for me
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          {/* the proof the footer promises: one move from the demo café, measured after it was taken */}
          <motion.div variants={briefItem} className="rounded-[12px] border border-[#E6E2DE] bg-[#FAF9F7] p-3" data-testid="sample-outcome">
            <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-[#2E6B4F]">
              Measured · {fmtCount(SAMPLE_OUTCOME.weeksLater)} weeks later
            </p>
            <p className="font-sans text-[13px] font-medium text-[#2D2A2E] leading-snug mt-0.5">
              {SAMPLE_OUTCOME.title} · taken {SAMPLE_OUTCOME.taken}
            </p>
            <p className="font-sans text-[12px] text-[#2D2A2E] mt-0.5 tabular-nums">
              Cups {fmtCount(SAMPLE_OUTCOME.cupsBefore)} → {fmtCount(SAMPLE_OUTCOME.cupsAfter)} · about <strong className="text-[#2E6B4F]">{fmtSigned(SAMPLE_OUTCOME.monthlySoFar)}/mo</strong> so far
            </p>
            <p className="font-sans text-[11.5px] text-[#6B6670] mt-1 leading-snug">
              Not a controlled test. Weather, a holiday or a new sign could explain part of it.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 2.3 }}
          className="px-5 py-3 bg-[#2E6B4F]/[0.06] border-t border-[#F0EDE9] flex items-center gap-2"
        >
          <Check size={14} strokeWidth={2.5} className="text-[#2E6B4F] flex-shrink-0" aria-hidden="true" />
          <p className="font-sans text-[11.5px] text-[#2D2A2E]">
            Every move you adopt gets <strong className="text-[#2E6B4F]">measured against reality</strong> — the ones that don&rsquo;t pan out count against the total.
          </p>
        </motion.div>
      </motion.div>

      <figcaption className="mt-3 text-center font-sans text-[11px] text-[#6B6670]">
        A sample brief — yours is computed from your own transactions.
      </figcaption>
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Static content
// ---------------------------------------------------------------------------

// The pitch steps in on a laptop; on a phone each line would wrap under its
// indent and the block turns ragged, so the indents start at `md`.
const PITCH = [
  { t: 'ClearPath reads every transaction overnight,', indent: '' },
  { t: 'emails you the few moves worth making today,', indent: 'md:ml-8' },
  { t: 'each with a real dollar figure,', indent: 'md:ml-14' },
  { t: 'then measured against what actually happened.', indent: 'md:ml-5' },
];

const LOOP = [
  { icon: Plug, k: 'Connect', t: 'Link your POS once', d: 'Square syncs automatically. Clover, Toast, Lightspeed and Shopify POS upload as a CSV, with live connections on the way.' },
  { icon: Moon, k: 'Analyze', t: 'ClearPath works overnight', d: 'Sixteen detectors read every sale for pricing, bundles, dead weight, timing, and anomalies, plus your supplier costs. All while you sleep.' },
  { icon: Sun, k: 'Decide', t: 'A short brief by morning', d: 'Up to three concrete moves, each with a dollar range computed from your own sales. One tap to accept, straight from the email.' },
  { icon: LineChart, k: 'Measure', t: 'The truth, weeks later', d: 'ClearPath checks what actually happened and keeps an honest score, including the moves that didn’t pan out.' },
];

const POS = ['Square', 'Clover', 'Toast', 'Lightspeed', 'Shopify POS', 'CSV'];

// Read by backend/tests/test_marketing_claims.py: `stat: '16', label: 'deterministic detectors…'`.
const STATS = [
  { stat: '16', label: 'deterministic detectors check your sales every night and your supplier costs every week.' },
  { stat: '2–3 weeks', label: 'of real sales measure every move you adopt. The ones that don’t pan out count against the total.' },
  { stat: '26 weeks', label: 'of a modeled café’s history in the live demo — open it and check the math yourself.' },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const loopRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: loopRef, offset: ['start 60%', 'end 60%'] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  const navLink = `inline-flex items-center min-h-[44px] text-sm font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors rounded-sm ${FOCUS}`;
  const footerLink = `inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-xs font-sans text-[#6B6670] hover:text-[#2D2A2E] transition-colors rounded-sm ${FOCUS}`;

  return (
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-[#FAF9F7] overflow-x-hidden">

      {/* ── Navbar ── */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-md bg-[#FAF9F7]/85 border-b border-[#E6E2DE]' : 'bg-transparent border-b border-transparent'}`}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <ClearPathLogo size="sm" showText />
          <div className="hidden md:flex items-center gap-8">
            <a href="#how" className={navLink}>How it works</a>
            <Link to="/features" className={navLink}>Features</Link>
            <Link to="/features#pricing" className={navLink}>Pricing</Link>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/features" className={`md:hidden ${navLink}`}>Features</Link>
            <button
              onClick={() => navigate('/auth')}
              className={`bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium px-5 min-h-[44px] text-sm transition-colors shadow-sm ${FOCUS}`}
            >
              Get started
            </button>
          </div>
        </div>
      </nav>

      <main>
      {/* ── Hero ── */}
      <section className="relative">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 pt-14 pb-10 sm:pt-20 lg:pt-16 lg:pb-12">
          {/* on a laptop the headline sits at the top beside the tall card, so it is read first;
              on narrower screens the columns stack and the card follows the pitch */}
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-10 items-center lg:items-start">
            <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.09 } } }} className="lg:pt-12">
              <motion.p variants={fadeUp} className="font-sans text-sm font-medium text-[#4A2D5E] mb-5">
                A decision engine for independent cafés &amp; coffee shops
              </motion.p>

              <motion.h1 variants={fadeUp} className="font-serif text-[2.75rem] leading-[1.05] sm:text-5xl md:text-6xl md:leading-[1.03] text-[#2D2A2E] tracking-[-0.025em]">
                Less dashboard.<br />
                <span className="text-[#4A2D5E]">More decision.</span>
              </motion.h1>

              {/* the demo is the only evidence a zero-customer product has, so it sits one click under
                  the headline; "How it works" jumps to the 4-step loop further down */}
              <motion.div variants={fadeUp} className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
                <Link
                  to="/demo"
                  className={`bg-[#4A2D5E] hover:bg-[#3D254D] text-white rounded-[8px] px-5 min-h-[44px] inline-flex items-center font-sans font-medium text-sm transition-colors ${FOCUS}`}
                >
                  See a live brief
                </Link>
                <a
                  href="#how"
                  className={`group inline-flex items-center min-h-[44px] font-sans text-sm font-medium text-[#4A2D5E] hover:text-[#3D254D] transition-colors rounded-sm ${FOCUS}`}
                >
                  <span className="border-b border-dashed border-[#4A2D5E]/50 group-hover:border-[#4A2D5E] pb-0.5">How it works</span>
                </a>
              </motion.div>

              {/* the pitch — in the open, no cover. One flowing paragraph on a phone
                  (four short lines wrapped into a ragged staircase there), the
                  stepped lines from md up. */}
              <motion.div variants={fadeUp} className="mt-5 max-w-md md:space-y-2 font-sans text-[1.05rem] md:text-[1.15rem] leading-relaxed text-[#4A4048] text-pretty">
                {PITCH.map((s) => (
                  <Fragment key={s.t}>
                    <p className={`inline md:block ${s.indent}`}>{s.t}</p>{' '}
                  </Fragment>
                ))}
              </motion.div>
            </motion.div>

            <div className="lg:pl-4">
              <AnimatedBrief />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
          >
            <span className="text-xs font-sans text-[#6B6670]">Works with</span>
            {POS.map((p) => (
              <span key={p} className="font-serif text-[#6B6670] text-lg">{p}</span>
            ))}
          </motion.div>

          {/* the price, in the words an owner uses; both numbers come from pricing.ts */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-5 text-center font-sans text-sm text-[#2D2A2E]"
            data-testid="price-line"
          >
            <strong className="font-medium">{PRO_PRICE_LABEL} a month; the first ten shops pay {FOUNDING_PRICE_LABEL}, locked for life</strong>
          </motion.p>
        </div>
      </section>

      {/* ── Time Machine band — backtest figures from the modeled demo café ── */}
      <section className="relative bg-[#4A2D5E] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.12] bg-[radial-gradient(circle_at_20%_20%,_white,_transparent_45%)]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 py-16 md:py-20 text-center">
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }} className="font-sans text-xs sm:text-sm text-white/70 uppercase tracking-[0.15em] mb-5">
            The Time Machine · run on our demo shop
          </motion.p>
          {/* a figure, so DM Sans (DESIGN.md: figures are never Lora) — and static, so it never reads $0 */}
          <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }} className="font-sans text-5xl md:text-6xl font-bold tabular-nums text-white tracking-tight">
            {fmt(2500)}+
          </motion.p>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} className="mt-5 font-sans text-white/75 text-base max-w-xl mx-auto leading-relaxed text-pretty">
            is the low-end floor of what ClearPath surfaces for <span className="text-white font-medium">Morning Ritual Coffee Co.</span> over
            26 weeks, across <span className="text-white font-medium">50+ concrete moves</span>. Morning Ritual is our
            modeled demo caf&eacute;, not a customer, and it&rsquo;s the demo account you can open right now. The same
            backtest runs on your own history the day you connect.
          </motion.p>
        </div>
      </section>

      {/* ── The loop (how it works) ── */}
      <section id="how" className="scroll-mt-16 max-w-5xl mx-auto px-5 sm:px-6 py-20 lg:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={{ visible: { transition: { staggerChildren: 0.08 } } }}>
          <motion.p variants={fadeUp} className="font-sans text-sm text-[#4A2D5E] font-medium text-center mb-3">The loop</motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-center text-[#2D2A2E] mb-12 md:mb-16 tracking-tight text-balance">
            Set it once. Read it every morning.
          </motion.h2>
        </motion.div>

        <div ref={loopRef} className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-[#E6E2DE]" aria-hidden="true">
            <motion.div style={{ scaleY: lineScale }} className="absolute inset-0 origin-top bg-[#4A2D5E]" />
          </div>

          <div className="space-y-10 md:space-y-0">
            {LOOP.map((s, i) => {
              const left = i % 2 === 0;
              return (
                <div key={s.k} className="md:grid md:grid-cols-2 md:gap-12 md:items-center md:min-h-[168px]">
                  <motion.div
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className={`relative ${left ? 'md:col-start-1 md:text-right md:pr-4' : 'md:col-start-2 md:pl-4'}`}
                  >
                    <div className={`hidden md:block absolute top-1/2 -translate-y-1/2 ${left ? '-right-[calc(3rem+1px)]' : '-left-[calc(3rem+1px)]'} w-3 h-3 rounded-full bg-[#4A2D5E] ring-4 ring-[#FAF9F7]`} aria-hidden="true" />
                    <div className={`inline-flex items-center gap-2.5 mb-3 ${left ? 'md:flex-row-reverse' : ''}`}>
                      <span className="w-9 h-9 rounded-[10px] bg-[#4A2D5E]/8 flex items-center justify-center">
                        <s.icon size={17} strokeWidth={1.5} className="text-[#4A2D5E]" aria-hidden="true" />
                      </span>
                      <span className="font-sans text-xs font-semibold uppercase tracking-wider text-[#4A2D5E]">{s.k}</span>
                    </div>
                    <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">{s.t}</h3>
                    <p className="font-sans text-sm text-[#6B6670] leading-relaxed max-w-sm md:inline-block">{s.d}</p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── The gap (stats) — the homepage ends here, on purpose ── */}
      <section className="bg-[#2D2A2E]">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-16 md:py-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-white text-center leading-tight mb-4 tracking-tight text-balance">
              Your POS collects everything.<br className="hidden sm:block" /> Almost none of it reaches you.
            </motion.h2>
            <motion.p variants={fadeUp} className="font-sans text-white/60 text-center max-w-xl mx-auto leading-relaxed mb-12 text-pretty">
              Every transaction is a signal. Together they say exactly what to change, push, and stop, but only if someone reads them.
            </motion.p>

            {/* three facts in one ledger row — figures in DM Sans, a hairline between them */}
            <motion.div variants={fadeUp} className="border-y border-white/15 divide-y divide-white/15 md:grid md:grid-cols-3 md:divide-y-0 md:divide-x">
              {STATS.map((item) => (
                <div key={item.stat} className="py-6 md:py-7 md:px-8 md:first:pl-0 md:last:pr-0">
                  <p className="font-sans text-4xl font-bold tabular-nums text-white tracking-tight">{item.stat}</p>
                  <p className="font-sans text-sm text-white/60 leading-relaxed mt-2 max-w-[30ch]">{item.label}</p>
                </div>
              ))}
            </motion.div>

            <motion.p variants={fadeUp} className="font-serif text-xl text-white/85 text-center mt-12">
              ClearPath closes the gap.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex items-center justify-center gap-x-6 gap-y-4 flex-wrap">
              <button
                onClick={() => navigate('/auth')}
                className={`inline-flex items-center gap-2 bg-white text-[#4A2D5E] hover:bg-[#FAF9F7] rounded-[8px] font-sans font-semibold px-7 py-3.5 text-sm transition-all shadow-lg hover:-translate-y-px ${FOCUS_ON_DARK}`}
              >
                Start for free
                <ArrowRight size={15} strokeWidth={2} aria-hidden="true" />
              </button>
              <button
                onClick={() => navigate('/demo')}
                className={`inline-flex items-center min-h-[44px] font-sans text-sm font-medium text-white/75 hover:text-white transition-colors rounded-sm ${FOCUS_ON_DARK}`}
              >
                See a live brief →
              </button>
              <Link
                to="/audit"
                className={`inline-flex items-center min-h-[44px] font-sans text-sm font-medium text-white/75 hover:text-white transition-colors rounded-sm ${FOCUS_ON_DARK}`}
              >
                Run the sales check on your export →
              </Link>
              <Link
                to="/features"
                className={`inline-flex items-center min-h-[44px] font-sans text-sm font-medium text-white/75 hover:text-white transition-colors rounded-sm ${FOCUS_ON_DARK}`}
              >
                Explore features &amp; pricing →
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-[#E6E2DE] bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-4">
            <ClearPathLogo size="sm" />
            <span className="text-xs font-sans text-[#6B6670]">&copy; {new Date().getFullYear()} Clear Path SMB LLC</span>
          </div>
          <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-5 gap-y-0">
            <Link to="/features" className={footerLink}>Features</Link>
            <Link to="/features#pricing" className={footerLink}>Pricing</Link>
            <a href="mailto:support@clearpathsmb.com" className={footerLink}>support@clearpathsmb.com</a>
            <Link to="/privacy" className={footerLink}>Privacy</Link>
            <Link to="/terms" className={footerLink}>Terms</Link>
          </nav>
        </div>
      </footer>
    </div>
    </MotionConfig>
  );
}
